/**
 * Analysis & Visualization — Action definitions for analysis operations
 *
 * Covers hotspot/cluster map generation, multi-protein scoring,
 * protein-ligand interaction fingerprints, and atom selection.
 *
 * @module gibbs/registry/actions/analysis
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── Cluster / Hotspot Analysis ────────────────────────────────────────────

export const makeClusterMap = {
    id: 'make-cluster-map',
    name: 'Generate Hotspot/Cluster Map',
    category: 'Analysis & Visualization',
    description: 'Compute binding hotspot density map from fragment poses. Identifies regions of the protein surface where multiple fragments cluster, indicating druggable sites.',
    inputs: [
        param('hotspotFragments', `${T.Fragment }[]`, 'Fragments to include in clustering'),
        param('caseData', T.CaseData, 'Protein context'),
    ],
    outputs: [
        param('clusterMap', 'object', 'Hotspot coordinates and density map'),
    ],
    parameters: [
        param('diversity', 'number', 'Diversity parameter for clustering', false, { defaultValue: 9 }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('fragment_map_available', 'Fragment map must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'MakeClusterMap',
    serverCommand: 'make-clustermap',
    jobType: null,
};

// ─── Compound Scoring ──────────────────────────────────────────────────────

export const scoreCompounds = {
    id: 'score-compounds',
    name: 'Multi-Protein Compound Scoring',
    category: 'Analysis & Visualization',
    description: 'Comprehensive scoring workflow: dock and score multiple compounds against multiple proteins for selectivity analysis. Uses DiffDock + AutoDock + solvation. Identifies top poses and optionally cleans up intermediate results.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to evaluate'),
        param('proteins', `${T.CaseData }[]`, 'Target and off-target proteins'),
        param('scoringParams', 'object', 'Scoring configuration'),
    ],
    outputs: [
        param('results', 'object', 'Per-compound, per-protein results: {topPose, topScore, errors, scoringPoses, dockingSites}'),
        param('errors', 'string[]', 'Any errors encountered'),
    ],
    parameters: [
        param('quick', 'boolean', 'Fast mode (fewer poses)', false, { defaultValue: false }),
        param('diffdockPoseCount', 'number', 'Number of DiffDock poses to generate', false, { defaultValue: 10 }),
        param('autodockPoseCount', 'number', 'Number of AutoDock poses to generate', false, { defaultValue: 10 }),
        param('autodockSpeed', 'string', 'AutoDock speed preset', false),
        param('cleanup', 'boolean', 'Remove intermediate poses after scoring', false, { defaultValue: true }),
    ],
    dependsOn: [],
    preconditions: [
        pre('multiple_proteins_loaded', 'Multiple proteins must be loaded for selectivity analysis'),
        pre('compound_loaded', 'At least one compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'long',
    status: 'implemented',
    userAction: 'ScoreCompounds',
    serverCommand: null,
    jobType: null,
};

// ─── Interaction Analysis ──────────────────────────────────────────────────

export const fetchProlifData = {
    id: 'fetch-prolif-data',
    name: 'Protein-Ligand Interaction Fingerprint (ProLIF)',
    category: 'Analysis & Visualization',
    description: 'Generate a ligand interaction diagram showing all protein-ligand contacts (hydrogen bonds, hydrophobic contacts, pi-pi stacking, salt bridges, etc.) for a compound in its binding site.',
    inputs: [
        param('compound', T.Compound, 'Ligand to analyze'),
    ],
    outputs: [
        param('html', 'string', 'Interactive HTML diagram'),
        param('pdbObj', T.PdbData, 'Binding site structure used for analysis'),
    ],
    parameters: [
        param('width', 'number', 'Diagram width in pixels', false),
        param('height', 'number', 'Diagram height in pixels', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'A compound must be loaded and positioned in the binding site'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'FetchProLifData',
    serverCommand: null,
    jobType: null,
};

// ─── Selection ─────────────────────────────────────────────────────────────

export const selectAtoms = {
    id: 'select-atoms',
    name: 'Select Atoms by Query',
    category: 'Analysis & Visualization',
    description: 'Select atoms/residues using a query language (e.g., "protein within 5 ligand", "resname ALA", compound names). Foundation for many analysis and export operations.',
    inputs: [
        param('query', T.QueryString, 'Selection query'),
        param('caseData', T.CaseData, 'Target case', false),
    ],
    outputs: [
        param('selection', T.AtomSelection, 'Server-side selection state'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein or compound must be loaded'),
    ],
    postconditions: [
        post('selection_active', 'An atom selection is active'),
    ],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'Select',
    serverCommand: 'select',
    jobType: null,
};

// ─── Default export: all actions in this category ──────────────────────────

export default [
    makeClusterMap,
    scoreCompounds,
    fetchProlifData,
    selectAtoms,
];
